/*!
 * @license
 * Copyright 2019 Alfresco Software, Ltd.
 * Extenden by SMT - Servicios de Informática y Comunicaciones, S.A. (www.smt.es)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
    Component, EventEmitter,
    Input, OnInit, Output, TemplateRef, ViewEncapsulation, OnDestroy
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute, Params } from '@angular/router';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AlfrescoApiService, AppConfigService, AppConfigValues, AuthenticationService, LoginErrorEvent, LoginSubmitEvent, LoginSuccessEvent, LogService, OauthConfigModel, PeopleContentService, TranslationService, UserPreferencesService } from '@alfresco/adf-core';
import { AldenService } from '../../services/alden.service';
import { SMTUsersService } from '../../services/users-service';
import { Group, GroupEntry } from '@alfresco/js-api';
import { AppStore, SetUserProfileAction, SnackbarErrorAction, SnackbarInfoAction } from '@alfresco/aca-shared/store';
import { Store } from '@ngrx/store';
import { SMTLoadingDialogComponent } from 'projects/alden-extension/src/lib/dialogs/loading/loading.dialog';
import { MatDialog } from '@angular/material/dialog';
import { APP_CONFIG_AUDIT_LOG_LOGIN, APP_CONFIG_TOTP_LOGIN } from '../../models/alden-constants';
import { LoginService } from '../../services/login.service';
import { SMTConfirmDialogComponent } from '../../dialogs/confirm/confirm.dialog';

// eslint-disable-next-line no-shadow
enum LoginSteps {
    Landing = 0,
    Checking = 1,
    Welcome = 2
}

interface ValidationMessage {
    value: string;
    params?: any;
}

@Component({
    selector: 'adf-login-alden',
    templateUrl: './login-alden.component.html',
    styleUrls: ['./login-alden.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'adf-login' }
})
export class LoginAldenComponent implements OnInit, OnDestroy {
    isPasswordShow: boolean = false;

    /**
     * Should the `Remember me` checkbox be shown? When selected, this
     * option will remember the logged-in user after the browser is closed
     * to avoid logging in repeatedly.
     */
    @Input()
    showRememberMe: boolean = true;

    /** Should the extra actions (`Need Help`, `Register`, etc) be shown? */
    @Input()
    showLoginActions: boolean = true;

    /** Sets the URL of the NEED HELP link in the footer. */
    @Input()
    needHelpLink: string = '';

    /** Sets the URL of the REGISTER link in the footer. */
    @Input()
    registerLink: string = '';

    /** Path to a custom logo image. */
    @Input()
    logoImageUrl: string = './assets/images/alfresco-logo.svg';

    /** Path to a custom background image. */
    @Input()
    backgroundImageUrl: string = './assets/images/background.svg';

    /** The copyright text below the login box. */
    @Input()
    copyrightText: string = '\u00A9 2016 Alfresco Software, Inc. All Rights Reserved.';

    /** Custom validation rules for the login form. */
    @Input()
    fieldsValidation: any;

    /** Route to redirect to on successful login. */
    @Input()
    successRoute: string = null;

    /** Emitted when the login is successful. */
    @Output()
    success = new EventEmitter<LoginSuccessEvent>();

    /** Emitted when the login fails. */
    @Output()
    loginError = new EventEmitter<LoginErrorEvent>();

    /** Emitted when the login form is submitted. */
    @Output()
    executeSubmit = new EventEmitter<LoginSubmitEvent>();

    implicitFlow: boolean = false;
    
    backgroundImageStyle: string;
    form: UntypedFormGroup;
    isError: boolean = false;
    errorMsg: string;
    actualLoginStep: any = LoginSteps.Landing;
    LoginSteps = LoginSteps;
    rememberMe: boolean = true;
    formError: { [id: string]: string };
    minLength: number = 2;
    lengthTotp: number = 6;
    footerTemplate: TemplateRef<any>;
    headerTemplate: TemplateRef<any>;
    data: any;
    totp: boolean;
    audit: boolean;

    private _message: { [id: string]: { [id: string]: ValidationMessage } };
    private onDestroy$ = new Subject<boolean>();

    constructor(
        private _fb: UntypedFormBuilder,
        private authService: AuthenticationService,
        private translateService: TranslationService,
        private logService: LogService,
        private router: Router,
        private appConfig: AppConfigService,
        private userPreferences: UserPreferencesService,
        private route: ActivatedRoute,
        private alfrescoApiService: AlfrescoApiService,
        private aldenService: AldenService,
        private loginService: LoginService,
        private peopleContentService: PeopleContentService,
        private usersService: SMTUsersService,
        private store: Store<AppStore>,
        private dialog: MatDialog
    ) {
    }

    ngOnInit() {
        this.backgroundImageStyle = this.backgroundImageUrl ? `url(${this.backgroundImageUrl})` : '';
        
        this.totp = this.aldenService.getAppConfigProp(APP_CONFIG_TOTP_LOGIN, false);
        this.audit = this.aldenService.getAppConfigProp(APP_CONFIG_AUDIT_LOG_LOGIN, false);
        this.initFormError();
        this.initFormFieldsDefault();
        this.initFormFieldsMessages();

        this.successRoute = this.appConfig.get<string>('successRoute', this.successRoute);

        if (this.authService.isLoggedIn()) {
            this.router.navigate([this.successRoute]);
        } else {

            if (this.authService.isOauth()) {
                const oauth: OauthConfigModel = this.appConfig.get<OauthConfigModel>(AppConfigValues.OAUTHCONFIG, null);
                if (oauth && oauth.silentLogin) {
                    this.redirectToImplicitLogin();
                } else if (oauth && oauth.implicitFlow) {
                    this.implicitFlow = true;
                }
            }

            this.route.queryParams.subscribe((params: Params) => {
                const url = params['redirectUrl'];
                const provider = this.appConfig.get<string>(AppConfigValues.PROVIDERS);

                if(url)
                    this.authService.setRedirect({ provider, url });
            });
        }

        if (this.fieldsValidation) {
            this.form = this._fb.group(this.fieldsValidation);
        }

        this.form.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(data => this.onValueChanged(data));
    }

    ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    submit() {
        this.onSubmit(this.form.value);
    }

    redirectToImplicitLogin() {
        this.alfrescoApiService.getInstance().oauth2Auth.implicitLogin();
    }

    /**
     * Method called on submit form
     *
     * @param values
     * @param event
     */
    onSubmit(values: any): void {
        this.disableError();

        const args = new LoginSubmitEvent({
            controls: { username: this.form.controls.username }
        });
        this.executeSubmit.emit(args);

        if (!args.defaultPrevented) {
            this.performLogin(values);
        }
    }

    implicitLogin() {
        if (this.authService.isLoggedIn()) {
            this.router.navigate([this.successRoute]);
        }
        this.authService.ssoImplicitLogin();
    }

    /**
     * The method check the error in the form and push the error in the formError object
     *
     * @param data
     */
    onValueChanged(data: any) {
        this.disableError();
        for (const field in this.formError) {
            if (field) {
                this.formError[field] = '';
                const hasError =
                    (this.form.controls[field].errors && data[field] !== '') ||
                    (this.form.controls[field].dirty &&
                        !this.form.controls[field].valid);
                if (hasError) {
                    for (const key in this.form.controls[field].errors) {
                        if (key) {
                            const message = this._message[field][key];
                            if (message && message.value) {
                                const translated = this.translateService.instant(message.value, message.params);
                                this.formError[field] += translated;
                            }
                        }
                    }
                }
            }
        }
    }

    private performLogin(values: any) {
        this.actualLoginStep = LoginSteps.Checking;
        if (!this.totp) {
            this.loginACA(values);
        }else{
            this.loginTotp(values);
        }
    }

    private loginACA(values: any) {
        this.authService.login(values.username, values.password, this.rememberMe).subscribe(
                (token: any) => {
                    const redirectUrl = this.authService.getRedirect();

                    this.actualLoginStep = LoginSteps.Welcome;
                    this.userPreferences.setStoragePrefix(values.username);
                    values.password = null;
                    this.success.emit(
                        new LoginSuccessEvent(token, values.username, null)
                    );

                    this.redirectLogin(redirectUrl);
                },
                (err: any) => {
                    this.actualLoginStep = LoginSteps.Landing;
                    this.errorMsg = this.resolveErrorMessage(err);
                    this.isError = true;
                    this.loginError.emit(new LoginErrorEvent(err));
                },
                () => this.logService.info('Login done')
            );
    }

    private loginTotp(values: any) {
        const dialogRef = this.dialog.open(SMTLoadingDialogComponent, { minWidth: '250px', disableClose: true });
        this.loginService.login(values.username, values.password, this.rememberMe, values.totp).subscribe(
            async (token: any) => {
                switch(token.type){
                    case "error":
                        this.actualLoginStep = LoginSteps.Landing;
                        this.errorMsg = this.resolveErrorMessage("error!!");
                        this.isError = true;
                        this.loginError.emit(new LoginErrorEvent(token.ticket));
                    break
                    default:
                        const redirectUrl = this.authService.getRedirect();

                        this.actualLoginStep = LoginSteps.Welcome;
                        this.userPreferences.setStoragePrefix(values.username);
                        values.password = null;
                        this.success.emit(
                            new LoginSuccessEvent(token, values.username, null)
                        );

                        /**ACCIONES ADICIONALES */
                        this.alfrescoApiService.getInstance().contentAuth.setTicket(token.ticket.entry.id);
                        const res = await this.usersService.listGroups('-me-');
                        const groupsEntries: GroupEntry[] = res.list.entries;
                        const groups: Group[] = groupsEntries ? groupsEntries.map((obj) => obj.entry) : [];
                        const person = await this.aldenService.getPersonDetails(true);
                        this.store.dispatch(new SetUserProfileAction({ person: person, groups }));
                        this.alfrescoApiService.reset();
                        this.peopleContentService.resetLocalCurrentUser();
                        this.alfrescoApiService.getInstance().contentAuth.saveUsername(token.ticket.entry.userId);
                        /** FIN ACCIONES ADICIONALES */
                        
                        this.redirectLogin(redirectUrl);
                        
                    break
                }   
                dialogRef.close();             
            },
            (err: any) => {
                this.actualLoginStep = LoginSteps.Landing;
                this.errorMsg = this.resolveErrorMessage(err);
                this.isError = true;
                this.loginError.emit(new LoginErrorEvent(err));
                dialogRef.close();
            },
            () => {
                this.logService.info('Login done');
                dialogRef.close();
            }
        );
    }
    /**
     * Redirect to app form login
     * OPTIONAL: If audit its enabled, get date
     * @param redirectUrl 
     */
    private async redirectLogin(redirectUrl: string){

        //Audit
        let lastLoginTime: string = '';
        let username: string = '';
        if(this.audit){
            await this.getUserAuditLogin().then(res => {
                lastLoginTime = res.lastLogin;
                username = res.user;
            });
        }

        //Redirecciona
        if (redirectUrl) {
            this.authService.setRedirect(null);
            this.router.navigateByUrl(redirectUrl);
        } else if (this.successRoute) {
            this.router.navigate([this.successRoute]);
            if(this.audit){
                this.dialog.open(SMTConfirmDialogComponent, 
                    { 
                        minWidth: '250px', 
                        disableClose: false,
                        data: {
                            title: lastLoginTime ? this.translateService.instant('ALDEN.LOGIN.AUDIT_DIALOG.TITLE'): this.translateService.instant('ALDEN.LOGIN.AUDIT_DIALOG.TITLE_FIST_LOGIN'),
                            htmlContent: lastLoginTime ? `<span>${this.translateService.instant('ALDEN.LOGIN.AUDIT_DIALOG.TEXT_USER')}: <b>${username}</b></span><br><span>${this.translateService.instant('ALDEN.LOGIN.AUDIT_DIALOG.TEXT_DATE')}: <b>${lastLoginTime}</b></span>` : `<span>${this.translateService.instant('ALDEN.LOGIN.AUDIT_DIALOG.TEXT_USER')}: <b>${username}</b></span>`,
                            hideCancelButton: true
                        }
                    });
            }
        }
    }

    /**
     * Check and display the right error message in the UI
     */
    private resolveErrorMessage(err: any): string {
        console.error("ERROR", err);
        const msg = err?.message || '';
        const status = err?.status;

        // Casos específicos
        if (err?.error?.crossDomain && msg.includes('Access-Control-Allow-Origin'))
            return err.error.message;

        if (status === 403 && msg.includes('Invalid CSRF-token'))
            return 'LOGIN.MESSAGES.LOGIN-ERROR-CSRF';

        if (status === 403 && msg.includes('The system is currently in read-only mode'))
            return 'LOGIN.MESSAGES.LOGIN-ECM-LICENSE';

        if (status === 500 && msg.includes('Internal Server Error'))
            return 'ALDEN.LOGIN.MESSAGES.LOGIN-ERROR-GENERIC';

        if (err?.requiredData && msg.includes('To send an email, enter your username and password'))
            return 'ALDEN.LOGIN.MESSAGES.LOGIN-ERROR-TOTP_REQUIRED_DATA_TO_EMAIL';

        // Casos genéricos
        if (this.totp)
            return 'ALDEN.LOGIN.MESSAGES.LOGIN-ERROR-TOTP_INVALID';

        if (status === 403)
            return 'LOGIN.MESSAGES.LOGIN-ERROR-CREDENTIALS';

        return 'ALDEN.LOGIN.MESSAGES.LOGIN-ERROR-GENERIC';
    }

    /**
     * Add a custom form error for a field
     *
     * @param field
     * @param msg
     */
    public addCustomFormError(field: string, msg: string) {
        this.formError[field] += msg;
    }

    /**
     * Add a custom validation rule error for a field
     *
     * @param field
     * @param ruleId - i.e. required | minlength | maxlength
     * @param msg
     */
    addCustomValidationError(
        field: string,
        ruleId: string,
        msg: string,
        params?: any
    ) {
        this._message[field][ruleId] = {
            value: msg,
            params
        };
    }

    /**
     * Display and hide the password value.
     */
    toggleShowPassword(event: Event) {
        event.stopPropagation();
        this.isPasswordShow = !this.isPasswordShow;
    }

    /**
     * The method return if a field is valid or not
     *
     * @param field
     */
    isErrorStyle(field: AbstractControl) {
        return !field.valid && field.dirty && !field.pristine;
    }

    /**
     * Trim username
     */
    trimUsername(event: any) {
        if(event.target)
            event.target.value = event.target.value.trim();
    }

    /**
     * Default formError values
     */
    private initFormError() {
        this.formError = {
            username: '',
            password: ''
        };
        if (this.totp) {
            this.formError['totp'] = '';
        }
    }

    /**
     * Init form fields messages
     */
    private initFormFieldsMessages() {
        this._message = {
            username: {
                required: {
                    value: 'LOGIN.MESSAGES.USERNAME-REQUIRED'
                },
                minlength: {
                    value: 'LOGIN.MESSAGES.USERNAME-MIN',
                    params: {
                        minLength: this.minLength
                    }
                }

            },
            password: {
                required: {
                    value: 'LOGIN.MESSAGES.PASSWORD-REQUIRED'
                }
            }
        };
        if (this.totp) {
            this._message['totp'] = {
                required: {
                    value: 'ALDEN.LOGIN.MESSAGES.LOGIN-ERROR-TOTP_REQUIRED'
                },
                pattern: {
                    value: 'ALDEN.LOGIN.MESSAGES.LOGIN-ERROR-TOTP_PATTERN'
                },
                minlength: {
                    value: 'ALDEN.LOGIN.MESSAGES.LOGIN-ERROR-TOTP_LENGTH',
                    params: {
                        minLength: this.lengthTotp
                    }
                },
                maxLength: {
                    value: 'ALDEN.LOGIN.MESSAGES.LOGIN-ERROR-TOTP_LENGTH',
                    params: {
                        maxLength: this.lengthTotp
                    }
                }
            }
        }
    }

    private initFormFieldsDefault() {
        this.form = this._fb.group({
            username: ['', Validators.compose([Validators.required, Validators.minLength(this.minLength)])],
            password: ['', Validators.required]
        });
        if (this.totp) {
            this.form.addControl('totp', this._fb.control('', Validators.compose([
                Validators.required,
                Validators.minLength(this.lengthTotp),
                Validators.maxLength(this.lengthTotp),
                Validators.pattern('^[0-9]*$')
            ])));
        }
    }

    /**
     * Disable the error flag
     */
    private disableError() {
        this.isError = false;
        this.initFormError();
    }

    /**
     * Enviar email para configurar TOTP
     */
    public sendTotpSetupEmail() {
        if(this.form.controls['username'].invalid || this.form.controls['password'].invalid){
            this.onValueChanged(this.form.value);
            this.isError = true;
            let error = {
                requiredData: true,
                message: "To send an email, enter your username and password"
            }
            this.errorMsg = this.resolveErrorMessage(error);
            return;
        }
        const dialogRef = this.dialog.open(SMTLoadingDialogComponent, { minWidth: '250px', disableClose: true });
        this.loginService.sendTotpSetupEmail(this.form.controls['username'].value, this.form.controls['password'].value).subscribe(
            async (resp: any) => {
                if(resp.status === 200){
                    dialogRef.close();
                    this.store.dispatch(new SnackbarInfoAction('ALDEN.LOGIN.MESSAGES.LOGIN-INFO-TOTP_EMAIL_SENT'));
                }else if(resp.status === 500){
                    this.isError = true;
                    this.errorMsg = this.resolveErrorMessage(resp);
                }else{
                    dialogRef.close();
                    this.store.dispatch(new SnackbarErrorAction('ALDEN.LOGIN.MESSAGES.LOGIN-INFO-TOTP_EMAIL_NOT_SENT'));
                }
            },
            (err: any) => {
                this.actualLoginStep = LoginSteps.Landing;
                this.errorMsg = this.resolveErrorMessage(err);
                this.isError = true;
                this.loginError.emit(new LoginErrorEvent(err));
                dialogRef.close();
            },
            () =>{ 
                this.logService.info('Login done');
                dialogRef.close();
            }
        );
    }

    
    /**
     * Obtiene la auditoria de inicia de sesión de el usuario actual
     *
     * Este método consulta el Core API de Alfresco
     * @returns Promise<any> Promesa que resuelve con la lista de grupos del usuario.
     */
    private getUserAuditLogin(): Promise<any> {
        const path = `smtecno/ssm/mfa/audit/user/last/login`;
        const httpMethod = 'GET';
        return this.aldenService.callAlfresco(path, httpMethod);
    }
}
